import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, tr } = useLang();

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const [feature, ...rest] = posts;

  return (
    <div data-testid="page-blog" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— JOURNAL / IDEE SULL'ARIA", en: "— JOURNAL / IDEAS ON AIR" })}
        </div>
        <h1 className="font-display text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter">
          <div><RevealLine delay={0.2}>{tr({ it: "Ricerche,", en: "Research," })}</RevealLine></div>
          <div>
            <RevealLine delay={0.35}>{tr({ it: "casi studio,", en: "case studies," })}</RevealLine>
          </div>
          <div>
            <RevealLine delay={0.5} className="font-editorial italic font-normal text-[#3f3f46]">
              {tr({ it: "visioni.", en: "visions." })}
            </RevealLine>
          </div>
        </h1>
        {lang === "en" && (
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#52525b] mt-8">
            Articles are currently available in Italian only.
          </p>
        )}
      </section>

      {/* Featured post */}
      {feature && (
        <section className="mt-24 border-t border-[#e4e4e7]">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16">
            <Link
              to={`/blog/${feature.slug}`}
              data-testid="blog-featured"
              className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
            >
              <div className="lg:col-span-7 relative aspect-[4/3] overflow-hidden border border-[#e4e4e7]">
                <img
                  src={feature.cover_image}
                  alt={feature.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
                />
                <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] text-white bg-black/50 px-3 py-2 backdrop-blur">
                  {tr({ it: "IN PRIMO PIANO", en: "FEATURED" })}
                </div>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-end">
                <div className="flex items-center gap-4 font-mono text-[10px] tracking-widest uppercase text-[#3f3f46] mb-6">
                  <span>{feature.category}</span>
                  <span>·</span>
                  <span>{feature.read_minutes} min</span>
                </div>
                <h2 className="font-display text-3xl md:text-5xl leading-[1] tracking-tighter mb-6 group-hover:text-[#007A8A] transition-colors">
                  {feature.title}
                </h2>
                <p className="font-body text-[#3f3f46] leading-relaxed mb-8">
                  {feature.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#0a0a0a]">
                  {tr({ it: "Leggi l'articolo", en: "Read the article" })} <ArrowUpRight size={12} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16">
          <div className="flex justify-between items-baseline mb-12">
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter">
              {tr({ it: "Tutti gli articoli", en: "All articles" })}
            </h3>
            <span className="font-mono text-[10px] tracking-widest text-[#52525b] uppercase">
              {loading
                ? tr({ it: "Caricamento…", en: "Loading…" })
                : `${posts.length} ${tr({ it: "articoli", en: "articles" })}`}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block border border-[#e4e4e7] hover:border-[#007A8A] transition-colors h-full bg-[#f4f4f5]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-4 font-mono text-[10px] tracking-widest uppercase text-[#3f3f46] mb-4">
                      <span>{p.category}</span>
                      <span>·</span>
                      <span>{p.read_minutes} min</span>
                    </div>
                    <h4 className="font-display text-2xl leading-[1.05] tracking-tighter mb-3 group-hover:text-[#007A8A] transition-colors">
                      {p.title}
                    </h4>
                    <p className="font-body text-sm text-[#3f3f46] leading-relaxed">
                      {p.excerpt}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
